/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import BaseService from '../../../services/BaseService';
import {
  REPROCESS_BATCH_LIMIT,
  SYNC_STATUS,
} from './constants';

const parseBatchPayload = (response) => {
  const data = response?.data ?? response ?? {};
  return {
    requested: Number(data.requested) || 0,
    success: Number(data.success) || 0,
    failed: Number(data.failed) || 0,
    remaining:
      data.remaining != null ? Number(data.remaining) : null,
    processed: Number(data.processed) || Number(data.requested) || 0,
  };
};

const ReprocessBatchDialog = ({
  open,
  apiFilter,
  initialTotal,
  onClose,
  onComplete,
}) => {
  const cancelledRef = useRef(false);
  const [phase, setPhase] = useState('idle');
  const [batchIndex, setBatchIndex] = useState(0);
  const [totalEstimate, setTotalEstimate] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [accSuccess, setAccSuccess] = useState(0);
  const [accFailed, setAccFailed] = useState(0);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    if (!open) return undefined;

    cancelledRef.current = false;
    setPhase('running');
    setBatchIndex(0);
    setTotalEstimate(initialTotal || 0);
    setProcessedCount(0);
    setAccSuccess(0);
    setAccFailed(0);
    setErrorMessage(null);

    let remaining = initialTotal > 0 ? initialTotal : 1;
    let total = initialTotal > 0 ? initialTotal : 1;
    let processed = 0;
    let successSum = 0;
    let failedSum = 0;
    let batch = 0;
    let cancelled = false;

    const run = async () => {
      try {
        while (remaining > 0 && !cancelled && !cancelledRef.current) {
          batch += 1;
          setBatchIndex(batch);

          const response = await BaseService.post({
            url: 'integrate-entities/reprocessar',
            data: {
              ...apiFilter,
              status: SYNC_STATUS.ERROR_SYNC,
              limit: REPROCESS_BATCH_LIMIT,
            },
          });

          const payload = parseBatchPayload(response);
          successSum += payload.success;
          failedSum += payload.failed;
          processed += payload.processed;

          if (payload.remaining != null) {
            remaining = payload.remaining;
            total = Math.max(total, processed + remaining);
          } else {
            remaining = payload.requested >= REPROCESS_BATCH_LIMIT
              ? remaining - payload.processed
              : 0;
          }

          setTotalEstimate(total);
          setProcessedCount(processed);
          setAccSuccess(successSum);
          setAccFailed(failedSum);

          if (payload.requested === 0) {
            break;
          }
        }

        if (cancelled || cancelledRef.current) {
          setPhase('cancelled');
        } else {
          setPhase('done');
        }
      } catch (error) {
        const msg =
          error?.response?.data?.message
          || error?.message
          || 'Falha ao reprocessar lote';
        setErrorMessage(msg);
        setPhase('error');
      }
    };

    run();

    return () => {
      cancelled = true;
      cancelledRef.current = true;
    };
  }, [open, apiFilter, initialTotal]);

  const handleCancel = () => {
    cancelledRef.current = true;
  };

  const handleClose = () => {
    if (phase === 'running') return;
    onClose();
    if (phase === 'done' || phase === 'cancelled' || phase === 'error') {
      onComplete();
    }
  };

  const progressPercent =
    totalEstimate > 0
      ? Math.min(100, Math.round((processedCount / totalEstimate) * 100))
      : 0;

  const isRunning = phase === 'running';

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (isRunning && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Reprocessando integrações com erro</DialogTitle>
      <DialogContent>
        {phase === 'running' && (
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Lote {batchIndex} — processando até {REPROCESS_BATCH_LIMIT} por vez.
              Não feche esta janela.
            </Typography>
            <LinearProgress
              variant={totalEstimate > 0 ? 'determinate' : 'indeterminate'}
              value={progressPercent}
              sx={{ mb: 1, height: 8, borderRadius: 1 }}
            />
            <Typography variant="body2">
              {totalEstimate > 0
                ? `${processedCount} de ${totalEstimate} (${progressPercent}%)`
                : `${processedCount} processadas`}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {accSuccess} com sucesso · {accFailed} com falha
            </Typography>
          </Box>
        )}

        {phase === 'done' && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Concluído: {accSuccess} reprocessadas com sucesso
            {accFailed > 0 ? `, ${accFailed} com falha` : ''}.
          </Alert>
        )}

        {phase === 'cancelled' && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Cancelado após o lote atual. {accSuccess} com sucesso, {accFailed} com falha
            ({processedCount} processadas no total).
          </Alert>
        )}

        {phase === 'error' && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {errorMessage}
            {processedCount > 0 && (
              <Typography variant="body2" component="div" sx={{ mt: 1 }}>
                Antes da falha: {accSuccess} ok, {accFailed} falhas ({processedCount} processadas).
              </Typography>
            )}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {isRunning ? (
          <Button color="inherit" onClick={handleCancel} variant="outlined">
            Cancelar
          </Button>
        ) : (
          <Button onClick={handleClose} variant="contained">
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ReprocessBatchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  apiFilter: PropTypes.object.isRequired,
  initialTotal: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
};

export default ReprocessBatchDialog;
